/**
 * Single source of truth (web) for push destinations. The service worker keeps
 * its own hard-coded copy (public/firebase-messaging-sw.js) because it cannot
 * import app modules; keep both in sync.
 */
export const PUSH_DESTINATION_PATHS: Readonly<Record<string, string>> = Object.freeze({
  DASHBOARD: "/dashboard",
  BOOSTER_LOAN: "/boosterloan",
  PORTFOLIO: "/portfolio",
  DISCOVERY: "/discovery",
  REFER_FRIEND: "/referfriend",
  TRANSFER: "/transfer",
  SETTINGS: "/settings",
});

export const DEFAULT_PUSH_PATH = "/dashboard";

/** Resolve a destination key to an allowlisted in-app path (default /dashboard). */
export function resolvePushDestination(destination?: string | null): string {
  if (typeof destination !== "string") return DEFAULT_PUSH_PATH;
  const key = destination.trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(PUSH_DESTINATION_PATHS, key)
    ? PUSH_DESTINATION_PATHS[key]
    : DEFAULT_PUSH_PATH;
}

/**
 * True only for same-origin relative in-app paths: starts with a single "/",
 * no protocol-relative "//", no backslashes, no control chars, no scheme.
 */
export function isSafeInternalPath(path: unknown): path is string {
  if (typeof path !== "string" || path.length === 0 || path.length > 512) return false;
  if (path[0] !== "/") return false;
  if (path[1] === "/" || path[1] === "\\") return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(path)) return false;
  return true;
}

/** Server-provided path if safe, otherwise the default fallback. */
export function safePathOrDefault(path: unknown): string {
  return isSafeInternalPath(path) ? path : DEFAULT_PUSH_PATH;
}
